import { Box, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HtmlWithLatex from "./HtmlWithLatex";

/**
 * Renders a cloze word-choice sub-question in exam-paper style.
 *
 * Matches the "Caught Out?" / "Performance Time" visual format from GL Assessment papers:
 *
 *   Waiting in the wings, the students' nerves soared as they listened to the
 *   ┌────────────┐ ┌────────┐ ┌─────────┐ ┌─────────┐ ┌─────────┐
 *   │ excitement │ │ excite │ │ exciting│ │ excited │ │ excites │
 *   │     A      │ │   B    │ │    C    │ │    D    │ │    E    │
 *   └────────────┘ └────────┘ └─────────┘ └─────────┘ └─────────┘
 *   whispers from the audience.
 *
 * Props:
 *   question      — sub-question object (question_text with [___], options array)
 *   selected      — currently selected option_text value (take mode)
 *   onSelect      — (value: string) => void  (take mode; omit for preview/review)
 *   showAnswers   — show correct/incorrect indicators (review/admin mode)
 *   studentAnswer — student's submitted answer (review mode)
 *   mode          — 'take' | 'review' | 'preview'
 */
const EnglishClozePassageLayout = ({
  question,
  selected,
  onSelect,
  showAnswers = false,
  studentAnswer,
  mode = "preview",
}) => {
  if (!question) return null;

  const options = question.options || [];
  const questionText = question.question_text || "";
  const effectiveAnswer = mode === "review" ? (studentAnswer ?? selected) : selected;

  // Determine correct option value(s)
  const correctValues = (() => {
    const correctOpts = options.filter((o) => o.is_correct);
    if (correctOpts.length > 0) return correctOpts.map((o) => o.option_text || o.text || "");
    const correctIndices = question.answer_data?.correct_options || [];
    return correctIndices.map((i) => {
      const opt = options[i];
      return opt ? (opt.option_text || opt.text || "") : "";
    });
  })();

  const correctIds = options.filter((o) => o.is_correct).map((o) => o.id).filter((id) => id != null);

  const isCorrectValue = (val, displayText) => {
    if (correctIds.length > 0 && val != null && correctIds.includes(val)) return true;
    const text = displayText ?? val;
    return correctValues.includes(text);
  };

  // Split question_text at [___] to get before/after parts
  const blankParts = questionText.split(/\[___\]|\[_+\]/);
  const beforeBlank = blankParts[0] || "";
  const afterBlank = blankParts[1] || "";

  // Build options with letter labels
  const labelledOptions = options.map((opt, idx) => {
    const displayText = opt.option_text || opt.text || "";
    return {
      ...opt,
      letter: String.fromCharCode(65 + idx),
      displayText,
      value: mode === "take" && opt.id ? opt.id : displayText,
    };
  });

  return (
    <Box sx={{ width: "100%" }}>
      {/* Passage context — text before the blank */}
      {beforeBlank.trim() && (
        <Box
          sx={{
            mb: 0.75,
            fontSize: "0.95rem",
            lineHeight: 1.7,
            color: "text.primary",
            fontFamily: '"Georgia", "Times New Roman", serif',
          }}
        >
          <HtmlWithLatex html={beforeBlank.trim()} />
        </Box>
      )}

      {/* Word-choice option boxes */}
      <Box
        sx={{
          display: "flex",
          gap: 0.75,
          flexWrap: "wrap",
          my: 1,
          alignItems: "flex-end",
        }}
      >
        {labelledOptions.map(({ letter, value, displayText }) => {
          const isSelected = effectiveAnswer === value;
          const isCorrect = isCorrectValue(value, displayText);
          const isWrong = showAnswers && isSelected && !isCorrect;
          const isRightAnswer = showAnswers && isCorrect;

          return (
            <Box
              key={letter}
              onClick={() => onSelect && onSelect(value)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                border: "1px solid",
                borderColor: isWrong
                  ? "error.main"
                  : isRightAnswer
                  ? "success.main"
                  : isSelected
                  ? "primary.main"
                  : "grey.500",
                borderRadius: 0.5,
                px: 1.25,
                py: 0.5,
                cursor: onSelect ? "pointer" : "default",
                bgcolor: isWrong
                  ? "#fef2f2"
                  : isRightAnswer
                  ? "#f0fdf4"
                  : isSelected
                  ? "#eff6ff"
                  : "white",
                minWidth: 52,
                transition: "all 0.15s",
                userSelect: "none",
                "&:hover": onSelect
                  ? {
                      borderColor: isWrong
                        ? "error.main"
                        : isRightAnswer
                        ? "success.main"
                        : "primary.main",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                    }
                  : {},
              }}
            >
              {/* Option text */}
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: isSelected ? 700 : 400,
                  color: isWrong
                    ? "error.main"
                    : isRightAnswer
                    ? "success.main"
                    : isSelected
                    ? "primary.main"
                    : "text.primary",
                  lineHeight: 1.3,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                }}
              >
                {displayText}
              </Typography>
              {/* Letter label */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, mt: 0.25 }}>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    color: isWrong
                      ? "error.main"
                      : isRightAnswer
                      ? "success.main"
                      : isSelected
                      ? "primary.main"
                      : "#1565c0",
                    lineHeight: 1,
                  }}
                >
                  {letter}
                </Typography>
                {isWrong && (
                  <CancelIcon sx={{ fontSize: 11, color: "error.main" }} />
                )}
                {isRightAnswer && (
                  <CheckCircleIcon sx={{ fontSize: 11, color: "success.main" }} />
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Passage context — text after the blank */}
      {afterBlank.trim() && (
        <Box
          sx={{
            mt: 0.75,
            fontSize: "0.95rem",
            lineHeight: 1.7,
            color: "text.primary",
            fontFamily: '"Georgia", "Times New Roman", serif',
          }}
        >
          <HtmlWithLatex html={afterBlank.trim()} />
        </Box>
      )}

      {/* Review: show correct answer when student got it wrong */}
      {showAnswers && effectiveAnswer && !isCorrectValue(effectiveAnswer) && correctValues.length > 0 && (
        <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Correct answer:
          </Typography>
          <Box
            sx={{
              border: "2px solid",
              borderColor: "success.main",
              borderRadius: 0.5,
              px: 1,
              py: 0.25,
              bgcolor: "#f0fdf4",
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: "success.dark", fontSize: "0.8rem" }}
            >
              {correctValues[0]}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default EnglishClozePassageLayout;
